import { useState, useCallback } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';

const getToken = () => window.localStorage.getItem('access_token');

const setToken = accessToken => {
  window.localStorage.setItem('access_token', accessToken);
};

const removeToken = () => {
  window.localStorage.removeItem('access_token');
};

/**
 * Returns information about
 */
const authorizationHeader = () => {
  return {
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  };
};

const rootUrl = process.env.REACT_APP_CORE_API_URL
  ? process.env.REACT_APP_CORE_API_URL
  : 'https://se1.lifenome.com/demo-api/user-api';

// Instance containing config for all requests
// Unavailable from outside
const axiosInstance = axios.create({
  baseURL: `${rootUrl}/`,
});

// Unique method to handle API errors accross whole app
// Could be moved somewhere else and check if Error Boundaries can be implemented on top of this
const handleErrors = error => {
  if (error == null) {
    return;
  }
  // Sent to Sentry any API related error which isn't caused by unauthorized user.
  // We have to check if error.response exists, because this part of the code can be executed even when there is no response from API.
  try {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      // pass 401 or 403
    } else {
      Sentry.withScope(scope => {
        if (error.response) {
          // this will allow us to easily filter errors in Sentry be tag containing HTTP status code
          scope.setTag('HTTP_status_code', error.response.status);
          if (error.response.data) {
            // this will allow us to see body of the response separately from all other data in this error event
            scope.setExtra('response_data', error.response.data);
          }
        }
        if (error.request) {
          // this will allow us to see the request data separately from all other data in this error event
          scope.setExtra('request_data', error.request);
        }
        // this will force the Sentry to group together only errors of the same type happening on the same URL
        scope.setFingerprint([window.location.pathname]);
        Sentry.captureException(error);
      });
    }
  } catch (e) {
    // pass because we aren't interested in error happening here
  }

  if (error.response) {
    // The request was made and the server responded with a status code that falls out of the range of 2xx
    console.debug('ERROR STATUS: ', error.response.status);
    console.debug(error.response);
    if (error.response.status === 400) {
      return error;
    }
    if (error.response.status === 401 || error.response.status === 403) {
      removeToken();
      window.location = '/?signin';
    } else {
      return error;
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    console.debug('No response');
    console.debug('Error request: ', error.request);
    throw new Error(error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.debug('Error message: ', error.message);
    throw new Error(error.message);
  }
  console.debug(error.config);
};

// Method for creating GET requests with implemented error handling
// Unavailable from outside
const axiosGET = async path => {
  let response = await axiosInstance
    .get(path, getToken() ? authorizationHeader() : undefined)
    .catch(error => {
      if (
        error &&
        error.response &&
        error.response.status === 404 &&
        (path.endsWith('profile/genotype/') ||
          path.indexOf('profile/reports/') !== -1)
      ) {
        // we aren't sending error 404 for GET 'profile/genotype/' or GET 'profile/reports/ to Sentry because
        // API returns 404 when there is no genotype for the user and this is a valid case, it isn't an error
        return;
      } else {
        let propagatedError = handleErrors(error);
        if (propagatedError.response) {
          throw propagatedError;
        }
      }
    });

  if (response) {
    return response.data;
  } else {
    return {};
  }
};

const axiosPUT = async (path, params) => {
  let response = await axiosInstance
    .put(path, params, getToken() ? authorizationHeader() : undefined)
    .catch(error => {
      let propagatedError = handleErrors(error);
      if (propagatedError.response) {
        throw propagatedError;
      }
    });

  if (response) {
    return response.data;
  } else {
    return null;
  }
};

const axiosPOST = async (path, params) => {
  let response = await axiosInstance
    .post(path, params, getToken() ? authorizationHeader() : undefined)
    .catch(error => handleErrors(error));

  if (response) {
    return response.data;
  } else {
    return {};
  }
};

const axiosPOSTNonBlocking = (path, params) => {
  return axiosInstance.post(
    path,
    params,
    getToken() ? authorizationHeader() : undefined
  );
};

const axiosPATCH = async (path, params) => {
  let response = await axiosInstance
    .patch(path, params, getToken() ? authorizationHeader() : undefined)
    .catch(error => handleErrors(error));

  if (response) {
    return response.data;
  } else {
    return {};
  }
};

const axiosDELETE = async path => {
  let response = await axiosInstance
    .delete(path, getToken() ? authorizationHeader() : undefined)
    .catch(error => {
      if (
        error &&
        error.response &&
        error.response.status === 404 &&
        path.indexOf('profile/reports/') !== -1
      ) {
        // we aren't sending error 404 for GET 'profile/reports/' to Sentry because
        // API returns 404 when there is no reports for the user and this is a valid case, it isn't an error
        return;
      } else {
        let propagatedError = handleErrors(error);
        if (propagatedError.response) {
          throw propagatedError;
        }
      }
    });
  if (response) {
    return response.data;
  } else {
    return {};
  }
};

const useUsersApi = () => {
  // state for getUserData
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // state for saveUserData
  const [saving, setSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState('');

  // state for getProfile
  const [responseProfile, setResponseProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [errorProfile, setErrorProfile] = useState('');

  // state for saveUserProfile
  const [savingProfile, setSavingProfile] = useState(false);
  const [errorSavingProfile, setErrorSavingProfile] = useState('');

  // state for getDiseasesData
  const [responseDiseases, setResponseDiseases] = useState(null);
  const [loadingDiseases, setLoadingDiseases] = useState(false);
  const [errorDiseases, setErrorDiseases] = useState('');

  // state for saveDiseasesDate
  const [savingDiseases, setSavingDiseases] = useState(false);
  const [errorSavingDiseases, setErrorSavingDiseases] = useState('');

  // state for getCovidLifestyle
  const [responseCovidLifestyle, setResponseCovidLifestyle] = useState(null);
  const [loadingCovidLifestyle, setLoadingCovidLifestyle] = useState(false);
  const [errorCovidLifestyle, setErrorCovidLifestyle] = useState('');

  // state for saveCovidLifestyle
  const [savingCovidLifestyle, setSavingCovidLifestyle] = useState(false);
  const [errorSavingCovidLifestyle, setErrorSavingCovidLifestyle] = useState(
    ''
  );

  // state for get ACE
  const [responseACE, setResponseACE] = useState(null);
  const [loadingACE, setLoadingACE] = useState(false);
  const [errorACE, setErrorACE] = useState('');

  // state for save ACE
  const [savingACE, setSavingACE] = useState(false);
  const [errorSavingACE, setErrorSavingACE] = useState('');

  // state for get ARB
  const [responseARB, setResponseARB] = useState(null);
  const [loadingARB, setLoadingARB] = useState(false);
  const [errorARB, setErrorARB] = useState('');

  // state for save ARB
  const [savingARB, setSavingARB] = useState(false);
  const [errorSavingARB, setErrorSavingARB] = useState('');

  // state for get Covid Past Infections
  const [
    responseCovidPastInfections,
    setResponseCovidPastInfections,
  ] = useState(null);
  const [loadingCovidPastInfections, setLoadingCovidPastInfections] = useState(
    false
  );
  const [errorCovidPastInfections, setErrorCovidPastInfections] = useState('');

  // state for save Covid Past Infections
  const [savingCovidPastInfections, setSavingCovidPastInfections] = useState(
    false
  );
  const [
    errorSavingCovidPastInfections,
    setErrorSavingCovidPastInfections,
  ] = useState('');

  // state for get Covid Symptoms
  const [responseCovidSymptoms, setResponseCovidSymptoms] = useState(null);
  const [loadingCovidSymptoms, setLoadingCovidSymptoms] = useState(false);
  const [errorCovidSymptoms, setErrorCovidSymptoms] = useState('');

  // state for save Covid-19 symptoms
  const [savingCovidSymptoms, setSavingCovidSymptoms] = useState(false);
  const [errorSavingCovidSymptoms, setErrorSavingCovidSymptoms] = useState('');

  // state for get Covid Comorbidities
  const [responseCovidComorbidities, setResponseCovidComorbidities] = useState(
    null
  );
  const [loadingCovidComorbidities, setLoadingCovidComorbidities] = useState(
    false
  );
  const [errorCovidComorbidities, setErrorCovidComorbidities] = useState('');

  // state for save Covid Comorbidities
  const [savingCovidComorbidities, setSavingCovidComorbidities] = useState(
    false
  );
  const [
    errorSavingCovidComorbidities,
    setErrorSavingCovidComorbidities,
  ] = useState('');

  // state for getGenotype
  const [responseGenotype, setResponseGenotype] = useState(null);
  const [loadingGenotype, setLoadingGenotype] = useState(false);
  const [errorGenotype, setErrorGenotype] = useState('');

  // state for saveGenotype
  const [savingGenotype, setSavingGenotype] = useState(false);
  const [errorSavingGenotype, setErrorSavingGenotype] = useState('');

  const [errorInitiatePayment, setErrorInitiatePayment] = useState('');

  // state for delete profile Heredity
  const [axiosDeleting, setAxiosDeleting] = useState(false);
  const [responseAxiosDelete, setResponseAxiosDelete] = useState(null);
  const [errorAxiosDelete, setErrorAxiosDelete] = useState(false);

  // state for getTraitAssessmentList
  const [
    responseTraitAssessmentList,
    setResponseTraitAssessmentList,
  ] = useState(null);
  const [loadingTraitAssessmentList, setLoadingTraitAssessmentList] = useState(
    false
  );
  const [errorTraitAssessmentList, setErrorTraitAssessmentList] = useState('');

  // state fot getSpareAssessments
  const [spareAssessments, setSpareAssessments] = useState(null);
  const [loadingSpareAssessments, setLoadingSpareAssessments] = useState(null);
  const [errorSpareAssessments, setErrorSpareAssessments] = useState('');

  // state holding the optional error during user data DELETE chain
  const [deletingChainError, setDeletingChainError] = useState(null);
  // state holding the flag indicating whether user data DELETE chain has finished (true = finished, false = in progress, null = unknown state)
  const [deletingChain, setDeletingChain] = useState(null);

  // state for getReports
  const [responseReports, setResponseReports] = useState(null);
  const [loadingReports, setLoadingReports] = useState(null);
  const [errorReports, setErrorReports] = useState('');

  // state for saveReports
  const [responseSavingReports, setResponseSavingReports] = useState(null);
  const [, setSavingReports] = useState(false);
  const [, setErrorSavingReports] = useState(null);

  // state for creating Email notification
  const [
    responseCreatingEmailNotifications,
    setResponseCreatingEmailNotification,
  ] = useState(null);
  const [, setCreatingEmailNotification] = useState(false);
  const [, setErrorCreatingEmailNotification] = useState(null);

  const apiUrl = `${rootUrl}/user/`;
  const apiProfileUrl = `${rootUrl}/profile/`;
  const apiAceUrl = `${rootUrl}/profile/covid_ace_medications/`;
  const apiArbUrl = `${rootUrl}/profile/covid_arb_medications/`;
  const apiCovidPastInfections = `${rootUrl}/profile/covid_past_infections/`;
  const apiCovidSymptops = `${rootUrl}/profile/covid_symptoms/`;
  const apiCovidComorbidities = `${rootUrl}/profile/covid_comorbidities/`;
  const apiGenotypeUrl = `${rootUrl}/profile/genotype/`;
  const apiProfileHeredity = `${rootUrl}/profile/heredity/`;
  const apiTraitAssessmentList = `${rootUrl}/profile/assessments/dna/`;
  const apiCovid19Risks = `${rootUrl}/profile/assessments/covid-19/`;
  const apiReports = `${rootUrl}/profile/reports`;
  const apiSpareAssessments = `${rootUrl}/profile/assessments/rms/spare/`;
  const apiDiseases = `${rootUrl}/profile/covid_diseases/`;
  const apiCovidLifestyle = `${rootUrl}/profile/covid_lifestyle/`;
  const apiGetPaymentDataUrl = `${rootUrl}/payments/braintree/inapp-purchases/`;
  const apiInitiatePaymentUrl = `${rootUrl}/payments/braintree/inapp-purchases/checkout`;
  const apiProductsUrl = `${rootUrl}/products/`;
  const apiEmailNotifications = `${rootUrl}/user/notify/emails`;

  const get = async (responseSetter, errorSetter, loadingSetter, url) => {
    errorSetter(null);
    loadingSetter(true);

    let response;
    try {
      response = await axiosGET(url);
      responseSetter(response);
    } catch (e) {
      const customErrorObject = {
        status: e.response ? e.response.status : e,
        details: e.request ? e.request.responseText : e,
      };
      errorSetter(customErrorObject);
    }

    loadingSetter(false);
    return response;
  };

  const put = async (errorSetter, loadingSetter, url, params) => {
    errorSetter(null);
    loadingSetter(true);

    let response;
    try {
      response = await axiosPUT(url, params);
    } catch (e) {
      errorSetter(`${e.status}: ${e.statusText}`);
    }

    loadingSetter(false);
    return response;
  };

  const post = async (errorSetter, loadingSetter, url, params) => {
    errorSetter(null);
    loadingSetter(true);

    let response;
    try {
      response = await axiosPOST(url, params);
    } catch (e) {
      errorSetter(`${e.status}: ${e.statusText}`);
    }

    loadingSetter(false);
    return response;
  };

  const del = async (errorSetter, loadingSetter, url, params) => {
    errorSetter(null);
    loadingSetter(true);

    let response;
    try {
      response = await axiosDELETE(url, params);
    } catch (e) {
      errorSetter(`${e.status}: ${e.statusText}`);
    }

    loadingSetter(false);
    return response;
  };

  const getUserData = async () => {
    return get(setResponse, setError, setLoading, apiUrl);
  };

  const saveUserData = async params => {
    setErrorSaving('');
    setSavingProfile(true);

    let response;
    try {
      response = await axiosPUT(apiUrl, params);
      setErrorSaving(null);
    } catch (e) {
      setErrorSaving(`${e.status}: ${e.statusText}`);
    }

    setSaving(false);
    return response;
  };

  const partialUpdateUserData = async params => {
    setErrorSaving(null);
    setSaving(true);

    let response;
    try {
      response = await axiosPATCH(apiUrl, params);
    } catch (e) {
      setErrorSaving(`${e.status}: ${e.statusText}`);
    }

    setSaving(false);
    return response;
  };

  const deleteUserData = async () => {
    let responseDeleteUserData = apiAxiosDelete(apiUrl);
    return responseDeleteUserData;
  };

  const getProfile = async () => {
    return get(
      setResponseProfile,
      setErrorProfile,
      setLoadingProfile,
      apiProfileUrl
    );
  };

  const saveProfile = async params => {
    setErrorSavingProfile('');
    setSavingProfile(true);

    let response;
    try {
      response = await axiosPUT(apiProfileUrl, params);
      setErrorSavingProfile(null);
    } catch (e) {
      setErrorSavingProfile(`${e.status}: ${e.statusText}`);
    }

    setSavingProfile(false);
    return response;
  };

  const deleteProfile = async () => {
    let responseDeleteProfile = apiAxiosDelete(apiProfileUrl);
    return responseDeleteProfile;
  };

  const getDiseases = async () => {
    return get(
      setResponseDiseases,
      setErrorDiseases,
      setLoadingDiseases,
      apiDiseases
    );
  };

  const saveDiseases = async params => {
    setErrorSavingDiseases('');
    setSavingDiseases(true);

    let response;
    try {
      response = await axiosPUT(apiDiseases, params);
      setErrorSavingDiseases(null);
    } catch (e) {
      setErrorSavingDiseases(`${e.status}: ${e.statusText}`);
    }

    setSavingDiseases(false);
    return response;
  };

  const deleteDiseases = async () => {
    let responseDeleteDiseases = apiAxiosDelete(apiDiseases);
    return responseDeleteDiseases;
  };

  const getCovidLifestyle = async () => {
    return get(
      setResponseCovidLifestyle,
      setErrorCovidLifestyle,
      setLoadingCovidLifestyle,
      apiCovidLifestyle
    );
  };

  const saveCovidLifestyle = async params => {
    setErrorSavingCovidLifestyle('');
    setSavingCovidLifestyle(true);

    let response;
    try {
      response = await axiosPUT(apiCovidLifestyle, params);
      setErrorSavingCovidLifestyle(null);
    } catch (e) {
      setErrorSavingCovidLifestyle(`${e.status}: ${e.statusText}`);
    }

    setSavingCovidLifestyle(false);
    return response;
  };

  const deleteCovidLifestyle = async () => {
    return apiAxiosDelete(apiCovidLifestyle);
  };

  const getACE = async () => {
    return get(setResponseACE, setErrorACE, setLoadingACE, apiAceUrl);
  };

  const saveACE = async params => {
    setErrorSavingACE('');
    setSavingACE(true);

    let response;
    try {
      response = await axiosPUT(apiAceUrl, params);
      setErrorSavingACE(null);
    } catch (e) {
      setErrorSavingACE(`${e.status}: ${e.statusText}`);
    }

    setSavingACE(false);
    return response;
  };

  const deleteACE = async () => {
    let responseDeleteACE = apiAxiosDelete(apiAceUrl);
    return responseDeleteACE;
  };

  const getARB = async () => {
    return get(setResponseARB, setErrorARB, setLoadingARB, apiArbUrl);
  };

  const saveARB = async params => {
    setErrorSavingARB('');
    setSavingARB(true);

    let response;
    try {
      response = await axiosPUT(apiArbUrl, params);
      setErrorSavingARB(null);
    } catch (e) {
      setErrorSavingARB(`${e.status}: ${e.statusText}`);
    }

    setSavingARB(false);
    return response;
  };

  const deleteARB = async () => {
    let responseDeleteARB = apiAxiosDelete(apiArbUrl);
    return responseDeleteARB;
  };

  const getCovidPastInfections = async () => {
    return get(
      setResponseCovidPastInfections,
      setErrorCovidPastInfections,
      setLoadingCovidPastInfections,
      apiCovidPastInfections
    );
  };

  const saveCovidPastInfections = async params => {
    setErrorCovidPastInfections('');
    setSavingCovidPastInfections(true);

    let response;
    try {
      response = await axiosPUT(apiCovidPastInfections, params);
      setErrorCovidPastInfections(null);
    } catch (e) {
      setErrorCovidPastInfections(`${e.status}: ${e.statusText}`);
    }

    setSavingCovidPastInfections(false);
    return response;
  };

  const deleteProfileCovidPastInfections = async () => {
    let responseDeleteCovidPastInfecions = apiAxiosDelete(
      apiCovidPastInfections
    );
    return responseDeleteCovidPastInfecions;
  };

  const getCovidSymptoms = async () => {
    return get(
      setResponseCovidSymptoms,
      setErrorCovidSymptoms,
      setLoadingCovidSymptoms,
      apiCovidSymptops
    );
  };

  const saveCovidSymptoms = async params => {
    setErrorCovidSymptoms('');
    setSavingCovidSymptoms(true);

    let response;
    try {
      response = await axiosPUT(apiCovidSymptops, params);
      setErrorCovidSymptoms(null);
    } catch (e) {
      setErrorCovidSymptoms(`${e.status}: ${e.statusText}`);
    }

    setSavingCovidSymptoms(false);
    return response;
  };

  const deleteProfileCovidSymptoms = async () => {
    let responseDeleteProfileCovidSymptoms = apiAxiosDelete(apiCovidSymptops);
    return responseDeleteProfileCovidSymptoms;
  };

  const getCovidComorbidities = async () => {
    return get(
      setResponseCovidComorbidities,
      setErrorSavingCovidComorbidities,
      setLoadingCovidComorbidities,
      apiCovidComorbidities
    );
  };

  const saveCovidComorbidities = async params => {
    setErrorSavingCovidComorbidities('');
    setSavingCovidComorbidities(true);

    let response;
    try {
      response = await axiosPUT(apiCovidComorbidities, params);
      setErrorSavingCovidComorbidities(null);
    } catch (e) {
      setErrorSavingCovidComorbidities(`${e.status}: ${e.statusText}`);
    }

    setSavingCovidComorbidities(false);
    return response;
  };

  const getGenotype = async () => {
    return get(
      setResponseGenotype,
      setErrorGenotype,
      setLoadingGenotype,
      apiGenotypeUrl
    );
  };

  const saveGenotype = async params => {
    setErrorSavingGenotype(null);
    setSavingGenotype(true);

    let responseSavingGenotype;
    try {
      responseSavingGenotype = await axiosPOST(apiGenotypeUrl, params);
    } catch (e) {
      setErrorSavingGenotype(`${e.status}: ${e.statusText}`);
    }

    setSavingGenotype(false);
    return responseSavingGenotype;
  };

  const deleteGenotype = async () => {
    let responseDeleteGenotype = apiAxiosDelete(apiGenotypeUrl);
    return responseDeleteGenotype;
  };

  const initiatePayment = async params => {
    setErrorInitiatePayment(null);

    try {
      return await axiosPOST(apiInitiatePaymentUrl, params);
    } catch (e) {
      setErrorInitiatePayment(`${e.status}: ${e.statusText}`);
    }
  };

  const getPaymentData = async () => {
    return axiosGET(apiGetPaymentDataUrl);
  };

  const getProductsData = async () => {
    return axiosGET(apiProductsUrl);
  };

  const getTraitAssessmentList = async () => {
    return get(
      setResponseTraitAssessmentList,
      setErrorTraitAssessmentList,
      setLoadingTraitAssessmentList,
      apiTraitAssessmentList
    );
  };

  const deleteProfileCovidComorbidities = async () => {
    let responseDeleteProfileCovidComorbidities = apiAxiosDelete(
      apiCovidComorbidities
    );
    return responseDeleteProfileCovidComorbidities;
  };

  const getSpareAssessments = async () => {
    return get(
      setSpareAssessments,
      setErrorSpareAssessments,
      setLoadingSpareAssessments,
      apiSpareAssessments
    );
  };

  const getReports = async () => {
    return get(
      setResponseReports,
      setErrorReports,
      setLoadingReports,
      `${apiReports}/spare/en/`
    );
  };

  const saveReports = params => {
    setErrorSavingReports(null);
    setSavingReports(true);

    return axiosPOSTNonBlocking(`${apiReports}/`, params)
      .then(response => {
        setResponseSavingReports(response);
        setSavingReports(false);
      })
      .catch(e => {
        setErrorSavingReports(`${e.status}: ${e.statusText}`);
        setSavingReports(false);
      });
  };

  const deleteReports = async () => {
    return apiAxiosDelete(`${apiReports}/spare/en/`);
  };

  const deleteAllUserData = () => {
    setDeletingChainError(null);
    setDeletingChain(true);
    // delete has finished when last delete action has finished so we will chain the DELETE requests
    deleteProfileCovidSymptoms()
      .then(() => {
        return deleteProfileCovidComorbidities();
      })
      .then(() => {
        return deleteProfile();
      })
      .then(() => {
        return deleteACE();
      })
      .then(() => {
        return deleteARB();
      })
      .then(() => {
        return deleteProfileCovidPastInfections();
      })
      .then(() => {
        return deleteDiseases();
      })
      .then(() => {
        return deleteCovidLifestyle();
      })
      .then(() => {
        return deleteUserData();
      })
      .then(() => {
        return deleteReports();
      })
      .then(() => {
        setDeletingChain(false);
      })
      .catch(error => {
        setDeletingChainError(error);
      });
  };

  const apiAxiosDelete = async url => {
    setErrorAxiosDelete(null);
    setAxiosDeleting(true);

    let responseAxiosDelete;
    try {
      responseAxiosDelete = await axiosDELETE(url);
      setResponseAxiosDelete(responseAxiosDelete);
    } catch (e) {
      setErrorAxiosDelete(`${e.status}: ${e.statusText}`);
    }

    setAxiosDeleting(false);
    return responseAxiosDelete;
  };

  const createEmailNotification = params => {
    setErrorCreatingEmailNotification(null);
    setCreatingEmailNotification(false);

    return axiosPOSTNonBlocking(`${apiEmailNotifications}/`, params)
      .then(response => {
        console.log(response);
        setResponseCreatingEmailNotification(response);
        setCreatingEmailNotification(false);
      })
      .catch(e => {
        setErrorCreatingEmailNotification(`${e.status}: ${e.statusText}`);
        setCreatingEmailNotification(false);
      });
  };

  return [
    {
      response,
      loading,
      error,
      saving,
      errorSaving,
      responseProfile,
      loadingProfile,
      errorProfile,
      savingProfile,
      errorSavingProfile,

      responseDiseases,
      loadingDiseases,
      errorDiseases,
      savingDiseases,
      errorSavingDiseases,

      responseCovidLifestyle,
      loadingCovidLifestyle,
      errorCovidLifestyle,
      savingCovidLifestyle,
      errorSavingCovidLifestyle,

      responseACE,
      loadingACE,
      errorACE,
      savingACE,
      errorSavingACE,
      responseARB,
      loadingARB,
      errorARB,
      savingARB,
      errorSavingARB,
      responseCovidPastInfections,
      loadingCovidPastInfections,
      errorCovidPastInfections,
      savingCovidPastInfections,
      errorSavingCovidPastInfections,
      responseCovidSymptoms,
      loadingCovidSymptoms,
      errorCovidSymptoms,
      savingCovidSymptoms,
      errorSavingCovidSymptoms,
      responseCovidComorbidities,
      loadingCovidComorbidities,
      errorCovidComorbidities,
      savingCovidComorbidities,
      errorSavingCovidComorbidities,
      responseGenotype,
      loadingGenotype,
      errorGenotype,
      savingGenotype,
      errorSavingGenotype,
      axiosDeleting,
      errorAxiosDelete,
      responseAxiosDelete,
      responseTraitAssessmentList,
      loadingTraitAssessmentList,
      errorTraitAssessmentList,
      spareAssessments,
      errorSpareAssessments,
      loadingSpareAssessments,
      responseReports,
      loadingReports,
      errorReports,
      responseSavingReports,
      deletingChainError,
      deletingChain,
      errorInitiatePayment,
      responseCreatingEmailNotifications,
      getUserData: useCallback(getUserData, []),
      saveUserData: useCallback(saveUserData, []),
      partialUpdateUserData: useCallback(partialUpdateUserData, []),
      getProfile: useCallback(getProfile, []),
      saveProfile: useCallback(saveProfile, []),
      getDiseases: useCallback(getDiseases, []),
      saveDiseases: useCallback(saveDiseases, []),
      getCovidLifestyle: useCallback(getCovidLifestyle, []),
      saveCovidLifestyle: useCallback(saveCovidLifestyle, []),
      getACE: useCallback(getACE, []),
      saveACE: useCallback(saveACE, []),
      getARB: useCallback(getARB, []),
      saveARB: useCallback(saveARB, []),
      getCovidPastInfections: useCallback(getCovidPastInfections, []),
      saveCovidPastInfections: useCallback(saveCovidPastInfections, []),
      getCovidSymptoms: useCallback(getCovidSymptoms, []),
      saveCovidSymptoms: useCallback(saveCovidSymptoms, []),
      getCovidComorbidities: useCallback(getCovidComorbidities, []),
      saveCovidComorbidities: useCallback(saveCovidComorbidities, []),
      getGenotype: useCallback(getGenotype, []),
      saveGenotype: useCallback(saveGenotype, []),
      deleteGenotype: useCallback(deleteGenotype, []),
      initiatePayment: useCallback(initiatePayment, []),
      getPaymentData: useCallback(getPaymentData, []),
      getProductsData: useCallback(getProductsData, []),
      getTraitAssessmentList: useCallback(getTraitAssessmentList, []),
      getSpareAssessments: useCallback(getSpareAssessments, []),
      getReports: useCallback(getReports, []),
      saveReports: useCallback(saveReports, []),
      deleteAllUserData: useCallback(deleteAllUserData, []),
      logout: useCallback(removeToken, []),
      createEmailNotification: useCallback(createEmailNotification, []),
    },
  ];
};

export default useUsersApi;
